import { create } from "zustand";

import { ListPillI, ListSubsectionI } from "shared/lib/interfaces/lists";
import { LIST_SUBSECTIONS_MAP } from "shared/lib/constants/lists";

import {
  ListColumnSelectOptionI,
  ListViewColumnFilterI,
  ListViewColumnI,
  ListViewColumnSortI,
  ListViewColumnUnassignedI,
  ListViewRowI,
  ListMetadataI,
} from "@/lib/types/list";
import { AvailableToCallColdLeadsForUserI } from "@/api/routes/list";

export interface ListsStoreI {
  lists?: ListPillI[];
  setLists: (lists?: ListPillI[]) => void;

  /**
   * Active list id might be different from the list id in the url query param.
   * This is because the user might have changed the active list id.
   *
   * or
   *
   * active list id is available earlier then both lists and active list objects
   */
  activeListId?: string;
  setActiveListId: (id?: string) => void;

  activeList?: ListPillI;
  setActiveList: (list?: ListPillI) => void;

  activeSubsection?: ListSubsectionI;
  setActiveSubsection: (subsection?: ListSubsectionI) => void;

  metadata?: ListMetadataI;
  setMetadata: (details?: ListMetadataI) => void;

  listViewId?: string;
  setListViewId: (id?: string) => void;

  filters?: ListViewColumnFilterI[];
  setFilters: (filters?: ListViewColumnFilterI[]) => void;

  searchTerm?: string;
  setSearchTerm: (term?: string) => void;

  sorts?: ListViewColumnSortI[];
  setSorts: (sorts?: ListViewColumnSortI[]) => void;

  columns?: ListViewColumnI[];
  setColumns: (columns?: ListViewColumnI[]) => void;

  columnSelectOptionsMap?: Record<string, ListColumnSelectOptionI[]>;
  setColumnSelectOptionsMap: (
    map: Record<string, ListColumnSelectOptionI[]>
  ) => void;

  rows?: ListViewRowI[];
  setRows: (rows?: ListViewRowI[]) => void;

  selectedRows?: ListViewRowI[];
  setSelectedRows: (rows?: ListViewRowI[]) => void;

  rowsNextToken?: string;
  setRowsNextToken: (nextToken?: string) => void;

  availableColumns?: ListViewColumnUnassignedI[];
  setAvailableColumns: (columns?: ListViewColumnUnassignedI[]) => void;

  selectedFilterColumns?: string[];
  setSelectedFilterColumns: (columns: string[]) => void;

  isRowsAPIPending?: boolean;
  setIsRowsAPIPending: (isPending?: boolean) => void;
  isRowsAPIFetching?: boolean;
  setIsRowsAPIFetching: (isFetching?: boolean) => void;

  isListsAPIPending?: boolean;
  setIsListsAPIPending: (isPending?: boolean) => void;
  isListsAPIFetching?: boolean;
  setIsListsAPIFetching: (isFetching?: boolean) => void;

  isMetadataAPIPending?: boolean;
  setIsMetadataAPIPending: (isPending?: boolean) => void;
  isMetadataAPIFetching?: boolean;
  setIsMetadataAPIFetching: (isFetching?: boolean) => void;

  availableToCallColdLeadsForUser?: AvailableToCallColdLeadsForUserI[];
  setAvailableToCallColdLeadsForUser: (
    availableToCallColdLeadsForUser?: AvailableToCallColdLeadsForUserI[]
  ) => void;
}

//TODO: get values from IndexDB
const initProps: Partial<ListsStoreI> = {
  lists: [],
  activeList: {} as ListPillI,
  activeSubsection: LIST_SUBSECTIONS_MAP.COLD_CALL,
  metadata: {} as ListMetadataI,

  filters: [],
  searchTerm: "",
  sorts: [],
  columns: [],
  columnSelectOptionsMap: {},
  rows: [],
  selectedRows: [],

  availableColumns: [],

  isListsAPIPending: false,
  isListsAPIFetching: false,
  isRowsAPIPending: false,
  isRowsAPIFetching: false,
  isMetadataAPIPending: false,
  isMetadataAPIFetching: false,

  availableToCallColdLeadsForUser: [],
};

/**
 * https://zustand.docs.pmnd.rs/guides/typescript
 */
export const useListsStore = create<ListsStoreI>()((set) => ({
  ...initProps,
  setLists: (lists) => set({ lists }),
  setActiveList: (list) => set({ activeList: list }),
  setActiveListId: (id) => set({ activeListId: id }),
  setActiveSubsection: (subsection) => set({ activeSubsection: subsection }),
  setMetadata: (metadata) => set({ metadata }),

  setListViewId: (id) => set({ listViewId: id }),

  setFilters: (filters) => set({ filters }),
  setSearchTerm: (term) => set({ searchTerm: term }),
  setSorts: (sorts) => set({ sorts }),
  setColumns: (columns) => set({ columns }),

  setSelectedFilterColumns: (columns) =>
    set({ selectedFilterColumns: columns }),

  setColumnSelectOptionsMap: (map) => set({ columnSelectOptionsMap: map }),

  setRows: (rows) => set({ rows }),
  setSelectedRows: (rows) => set({ selectedRows: rows }),
  setRowsNextToken: (nextToken) => set({ rowsNextToken: nextToken }),

  setAvailableColumns: (columns) => set({ availableColumns: columns }),

  setIsListsAPIPending: (isPending) => set({ isListsAPIPending: isPending }),
  setIsListsAPIFetching: (isFetching) =>
    set({ isListsAPIFetching: isFetching }),
  setIsRowsAPIPending: (isPending) => set({ isRowsAPIPending: isPending }),
  setIsRowsAPIFetching: (isFetching) => set({ isRowsAPIFetching: isFetching }),
  setIsMetadataAPIPending: (isPending) =>
    set({ isMetadataAPIPending: isPending }),
  setIsMetadataAPIFetching: (isFetching) =>
    set({ isMetadataAPIFetching: isFetching }),

  setAvailableToCallColdLeadsForUser: (availableToCallColdLeadsForUser) =>
    set({ availableToCallColdLeadsForUser }),
}));
